import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Button } from "@mui/material";
import type { DOMConversionMap, ElementFormatType, NodeKey } from "lexical";
import { $getNodeByKey } from "lexical";
import type { FC } from "react";
import { useState } from "react";
import type { EmailButtonType } from "../../../email-design/types/email-button-type";
import { AddStylableButtonForm } from "../components/button/add-styleable-button-form";
import { DeleteButtonDialog } from "../components/button/delete-button-dialog";
import { EditorActionButtons } from "../components/button/editor-action-buttons";
import { useLexicalContext } from "../lexical-context";
import type { StylableButtonForm } from "../types/styleable-button";
import type { SerializedStylableButtonNode } from "./stylable-button-node";
import { $isStylableButtonNode, StylableButtonNode } from "./stylable-button-node";
import { convertButtonElement, isButtonStylableButtonElement } from "./utils";

const StylableButton: FC<{
  nodeKey: NodeKey;
  text: string;
  url: string;
  fullWidth: boolean;
  buttonType: EmailButtonType;
  format?: ElementFormatType;
}> = ({ nodeKey, text, url, buttonType, fullWidth = false, format = "left" }) => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editor] = useLexicalComposerContext();
  const { filterTags } = useLexicalContext();

  const handleEdit = (data: StylableButtonForm): void => {
    data &&
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isStylableButtonNode(node)) return;
        else node.setButtons(data);
      });
    setOpenEditModal(false);
  };

  const handleDelete = (): void => {
    editor.update(() => {
      $getNodeByKey(nodeKey)?.remove();
    });
    setOpenDeleteModal(false);
  };

  return (
    <>
      <BlockWithAlignableContents
        className={{
          base: "",
          focus: "",
        }}
        format={format}
        nodeKey={nodeKey}
      >
        <EditorActionButtons
          buttons={["edit", "delete"]}
          disabled={!editor.isEditable()}
          onEditClicked={() => setOpenEditModal(true)}
          onDeleteClicked={handleDelete}
          fullWidth={fullWidth}
          nodeKey={nodeKey}
          data-analytics-id="lexical-stylable-button"
        >
          <Button
            data-analytics-id="lexical-stylable-button-preview"
            className={buttonType}
            fullWidth={fullWidth}
          >
            {text}
          </Button>
        </EditorActionButtons>
      </BlockWithAlignableContents>
      <AddStylableButtonForm
        open={openEditModal}
        filterTags={filterTags}
        onCancel={() => {
          setOpenEditModal(false);
        }}
        onConfirm={handleEdit}
        initialState={{
          text,
          url,
          buttonType,
          fullWidth,
          format,
        }}
      />
      <DeleteButtonDialog
        open={openDeleteModal}
        onCancel={() => {
          setOpenDeleteModal(false);
        }}
        onConfirm={handleDelete}
      />
    </>
  );
};

export class StylableButtonNodeMui extends StylableButtonNode {
  decorate(): React.JSX.Element {
    return (
      <StylableButton
        nodeKey={this.__key}
        text={this.__text}
        url={this.__url}
        buttonType={this.__buttonType}
        fullWidth={this.__fullWidth}
        format={this.__format}
      />
    );
  }
  static importJSON(serializedNode: SerializedStylableButtonNode): StylableButtonNode {
    return super.importJSON(serializedNode);
  }
  exportJSON(): SerializedStylableButtonNode {
    return super.exportJSON();
  }
  static clone(node: StylableButtonNode): StylableButtonNode {
    return super.clone(node);
  }
  static getType(): string {
    return super.getType();
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (imageNode: HTMLElement) => {
        if (!isButtonStylableButtonElement(imageNode)) return null;

        return {
          conversion: (n) => convertButtonElement(n, StylableButtonNodeMui),
          priority: 0,
        };
      },
    };
  }
}

export function $createStylableButtonNode(
  headless: boolean,
  text: string,
  url: string,
  buttonType: EmailButtonType,
  fullWidth: boolean,
  format: ElementFormatType,
): StylableButtonNode {
  return new (headless ? StylableButtonNode : StylableButtonNodeMui)(
    text,
    url,
    buttonType,
    fullWidth,
    format,
  );
}
